<template>
  <GenericSideMenu
    position="right"
    class="MiniCart"
    content-class="!p-0"
    :title="`${$t('minicart.headline')}`"
    :title-meta="cartItems"
    :title-description="titleDescription"
    @close="close"
  >
    <LoadBasketById v-if="userStore.isCustomerSuccessUser || userStore.isSalesRepUser" />

    <div v-if="isOrderProposalBasket" class="type-xs text-lightest px-12 desk:px-24 pt-12 desk:pt-24">
      <div class="flex justify-center items-center bg-purplePrimary h-32">
        <div v-if="orderProposalId" class="flex flex-row flex-wrap gap-8">
          <div>
            {{ $t('siteNotice.editingOrderProposal') }} #{{ orderProposalId }}
          </div>
          <div class="btn--text btn--sm btn--inv" @click="clearOrderProposalForSalesRep()">
            {{ $t('checkout.orderProposal.cancel') }}
          </div>
        </div>
        <div v-else class="flex ml-12 gap-8">
          {{ $t('siteNotice.editingOrderProposalCustomer') }}  | <span class="underline cursor-pointer" @click="clearOrderProposalForCustomer()">{{ $t('checkout.orderProposal.cancel') }}</span>
        </div>
      </div>
    </div>

    <CopiedFromOrderBanner
      v-if="copiedFromOrderId"
      :order-id="copiedFromOrderId"
      class="px-12 desk:px-24 pt-12 desk:pt-24"
    />

    <div
      v-if="cartStore.items.length"
      class="p-12 desk:p-24"
      :class="{ 'pt-24': orderProposalId }"
    >
      <div>
        <div class="mb-4 type-sm">
          {{ freeShippingProgress < 1 ? $t('minicart.progressbar.left', {sum: useLocalizedCurrency(leftToFreeShipping)}): $t('minicart.progressbar.full') }}
        </div>
        <div class="h-4 w-full bg-light">
          <div
            class="h-full w-full bg-mintPrimary"
            :class="{
              'bg-light': freeShippingProgress === 1,
              'bg-mintPrimary': freeShippingProgress !== 1
            }"
            :style="`width: ${freeShippingProgress * 100}%`"
          />
        </div>
      </div>

      <CrossdockingBanner v-if="isBackorderBasket && globalContent.getAllowBackorders" />

      <button
        v-if="useRuntimeConfig().public.features.fastOrder && userStore.isSalesRepUser"
        class="btn btn--secondary w-full mt-12"
        @click="uiStore.setShowScanner(true)"
      >
        <img
          alt=""
          src="/icons/barcode-read.svg"
          class="inline-block w-16 h-16 mr-12"
        >
        {{ $t('minicart.barcode.scan') }}
      </button>
      <MiniCartProduct
        v-for="(item, index) in cartStore.items"
        :key="`${item.lineNo}-${index}-${item.quantity}-${cartItems}`"
        :item="item"
        :hide-cross-docking="!globalContent.getAllowBackorders || userStore.isSalesRepUser || userStore.isCustomerSuccessUser || cartStore.items.length < 2"
      />
      <div
        class="flex flex-col gap-12 desk:gap-16 py-12 desk:py-16 type-xs desk:type-sm leading-single"
        :class="{
          'opacity-50': cartStore.isLoading
        }"
      >
        <div v-if="showPoints" class="flex justify-between">
          <div class="flex items-center">
            <div>
              {{ $t('minicart.summary.points') }}
            </div>
            <Tooltip class="ml-6" :text="$t('minicart.summary.pointsInfo', {amount: globalContent.getPointCollectingThreshold})">
              <img
                class="w-12 h-12 desk:w-16 desk:h-16"
                src="/icons/info.svg"
                alt=""
              >
            </Tooltip>
          </div>
          <div class="pl-16 font-medium text-right">
            {{ `${cartStore.cart.summary.points} ${$t('minicart.summary.pointsSuffix')}` }}
          </div>
        </div>
        <div v-if="showPoints" class="color-light h-1 w-full" />
        <div
          v-for="fee in cartStore.cart.summary?.additionalFees"
          :key="fee.name"
          class="flex justify-between"
        >
          <div>{{ fee.name }}</div>
          <div class="pl-16 font-medium text-right">
            {{ fee.feeBeforeVatDisplay }}
          </div>
        </div>
        <div class="flex justify-between">
          <div>{{ $t('minicart.summary.sum') }}</div>
          <div class="pl-16 font-medium text-right">
            {{ cartStore.cart.summary?.subtotalBeforeVatDisplay }}
          </div>
        </div>
        <div class="flex justify-between">
          <div>{{ $t('minicart.summary.freight') }}</div>
          <div class="pl-16 font-medium text-right">
            <span>{{ cartStore.cart.summary?.shippingBeforeVatDisplay }}</span>
          </div>
        </div>
        <div v-if="cartStore.cart.summary?.crossDockingShippingBeforeVat" class="flex justify-between">
          <div>{{ $t('crossDocking.shipping') }}</div>
          <div class="pl-16 text-right">
            <span>{{ cartStore.cart.summary?.crossDockingShippingBeforeVatDisplay }}</span>
          </div>
        </div>
        <div v-if="cartStore.cart.summary.vatDisplay" class="flex justify-between">
          <div>{{ $t('minicart.summary.vat') }}</div>
          <div class="pl-16 font-medium text-right">
            ({{ cartStore.cart.summary.vatDisplay.trim() }})
          </div>
        </div>
        <div
          v-if="cartStore.hasDiscount"
          class="flex justify-between"
        >
          <div>{{ $t('minicart.summary.discount') }}</div>
          <div class="pl-16 font-medium text-right text-criticalDark">
            -{{ cartStore.cart.summary.discountDisplay.trim() }}
          </div>
        </div>
        <div class="color-light h-1 w-full" />
        <div>
          <div class="flex justify-between items-end mb-8 type-sm desk:type-base leading-single">
            <div>{{ $t('minicart.summary.exVat') }}</div>
            <div class="type-headline-lg desk:type-headline-xl leading-single pl-16 text-right">
              <LoadingIcon v-if="cartStore.isLoading" class="relative -top-2" />
              <span v-else>{{ cartStore.cart.summary.priceBeforeVatDisplay }}</span>
            </div>
          </div>
          <div class="flex justify-between items-end text-dark">
            <div>{{ $t('minicart.summary.incVat') }}</div>
            <div class="pl-16 text-right">({{ cartStore.cart.summary.priceDisplay.trim() }})</div>
          </div>
        </div>
        <nuxt-link
          :to="manualUrl('/checkout')"
          class="btn btn--lg w-full group"
          :class="{
            'loading': cartStore.isLoading
          }"
        >
          <img
            alt=""
            src="/icons/arrow-right-inv.svg"
            class="inline-block w-16 h-16 mr-12 group-hover:translate-x-4 transition-all duration-200 ease-out"
          >
          {{ $t('minicart.toCheckout') }}
        </nuxt-link>
      </div>
    </div>
    <div v-else class="flex flex-col items-center justify-center p-24 h-full w-full relative pb-88 type-sm-medium">
      {{ $t('minicart.empty') }}
      <button
        v-if="useRuntimeConfig().public.features.fastOrder && userStore.isSalesRepUser"
        class="btn btn--secondary w-full mt-16"
        @click="uiStore.setShowScanner(true)"
      >
        <img
          alt=""
          src="/icons/barcode-read.svg"
          class="inline-block w-16 h-16 mr-12"
        >
        {{ $t('minicart.barcode.scan') }}
      </button>
    </div>
  </GenericSideMenu>
</template>

<script setup lang="ts">
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import MiniCartProduct from '~/components/cart/MiniCartProduct.vue';
import { useCartStore } from '~/store/cart';
import { storeToRefs } from 'pinia';

import { useUiStore } from '~/store/ui';
import { createEventFromCart } from '~/composeables/useTracking';

import { useLocalizedCurrency } from '~/composeables/useLocalizedCurrency';
import useLinkReplacer from '~/composeables/useLinkReplacer';
import { useGlobalContentStore } from '~/store/globalContent';
import Tooltip from '~/components/body/Tooltip.vue';
import { useUserStore } from '~/store/user';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';
import LoadBasketById from '~/components/account/LoadBasketById.vue';
import CrossdockingBanner from '~/components/cart/CrossdockingBanner.vue';
import CopiedFromOrderBanner from '~/components/cart/CopiedFromOrderBanner.vue';

const cartStore = useCartStore();
const userStore = useUserStore();
const { cartItems } = storeToRefs(cartStore);
const { $toNumber } = useNuxtApp();
const uiStore = useUiStore();
const globalContent = useGlobalContentStore();

const { manualUrl } = useLinkReplacer();
const close = () => {
  uiStore.setShowMiniCart(false);
};

const emit = defineEmits(['closeMenu']);

onMounted(()=> {
  createEventFromCart('view_cart');
});

const titleDescription = computed(() => {
  if (userStore.isSalesRepUser && cartStore.cart.norceBasketId) {
    return `#${ cartStore.cart.norceBasketId }`;
  }
  return '';
});

const clearOrderProposalForSalesRep = async() => {
  cartStore.deleteCart();
  uiStore.closeAllModal();
  emit('closeMenu');
  await userStore.setCurrentlyEditingOrderProposal(null);
};

const clearOrderProposalForCustomer = async() => {
  cartStore.deleteCart();
  uiStore.closeAllModal();
};

const freeShippingBreakpoint = globalContent.getFreeShippingThreshold;

const totalPrice = computed(() => {
  return $toNumber(cartStore.cart.summary.priceBeforeVat) - $toNumber(cartStore.cart.summary.shippingBeforeVat);
});

const freeShippingProgress = computed(() => {
  return totalPrice.value < freeShippingBreakpoint ? totalPrice.value / freeShippingBreakpoint : 1;
});

const leftToFreeShipping = computed(() => {
  return freeShippingBreakpoint - totalPrice.value;
});

const isOrderProposalBasket = computed(() => {
  return !!(userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null) || cartStore.isOrderProposalBasket;
});

const orderProposalId = computed(() => {
  return userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null;
});

const copiedFromOrderId = computed(() => {
  if (userStore.isSalesRepUser || userStore.isCustomerSuccessUser) {
    return cartStore.getCopiedFromOrderId;
  }
  return null;
});

const showPoints = computed(() => {
  return userStore.allowPointsPurchase && globalContent.getPointCollectingThreshold <= totalPrice.value;
});

const isBackorderBasket = computed(() => {
  return cartStore.cart.items.some((item) => item.stockStatus.value < 1);
});

</script>

<style scoped lang="postcss">
</style>
